import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Fingerprint, Filter, RotateCcw, ChevronLeft, ChevronRight, Loader2, Eye, PencilLine, CheckCircle2, Clock, AlertTriangle, FileSpreadsheet, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUS_LABEL = {
  DONE: "Selesai", IN_PROGRESS: "Belum Pulang", PERLU_VERIFIKASI: "Perlu Verifikasi",
  HADIR: "Hadir", TERLAMBAT: "Terlambat", ALPA: "Alpa",
  IZIN: "Izin", SAKIT: "Sakit", CUTI: "Cuti",
  BELUM_CHECKIN: "Belum Check-in", BELUM_PULANG: "Belum Pulang",
};
const STATUS_CLS = {
  DONE: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  IN_PROGRESS: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
  PERLU_VERIFIKASI: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  HADIR: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  TERLAMBAT: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  ALPA: "bg-[#FEE2E2] text-[#7F1D1D] border-[#FCA5A5]",
  IZIN: "bg-[#DBEAFE] text-[#1E3A8A] border-[#BFDBFE]",
  SAKIT: "bg-[#FFE4E6] text-[#9F1239] border-[#FECDD3]",
  CUTI: "bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]",
  BELUM_CHECKIN: "bg-[#FEE2E2] text-[#7F1D1D] border-[#FCA5A5]",
  BELUM_PULANG: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
};
const SOURCE_LABEL = {
  manual: "Manual",
  automatic: "Otomatis",
  automatic_fallback_last_inside: "Otomatis — Last Inside",
  automatic_rollover: "Otomatis — Rollover",
  admin_correction: "Koreksi Admin",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" }); }
  catch { return "—"; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dt = d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Jakarta" });
    const tm = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" });
    return `${dt} ${tm}`;
  } catch { return "—"; }
}

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
// First day of the current month in Asia/Jakarta — used as the default
// `start_date` filter so admins/koord land on a month-to-date view when
// opening the Rekap. `today()` already returns `YYYY-MM-DD` in WIB so we
// just swap the day segment to `01`.
const firstOfMonth = () => today().slice(0, 8) + "01";

// Feb 2026 — Quick-jump preset helpers (WIB-based, TZ-safe via UTC math
// on calendar-day counters). Preset value adalah DERIVED — dihitung
// dari `filters.start_date/end_date` (bukan state terpisah) sehingga
// sinkron otomatis ketika user mengubah tanggal manual → "Custom".
const _parseYmd = (ymd) => {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  return { y, m, d };
};
const _fmtYmd = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const _daysInMonth = (y, m) => new Date(y, m, 0).getDate(); // m: 1-12
const computeThisMonth = () => {
  const { y, m } = _parseYmd(today());
  return { start_date: _fmtYmd(y, m, 1), end_date: today() };
};
const computeLastMonth = () => {
  const { y, m } = _parseYmd(today());
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  return { start_date: _fmtYmd(py, pm, 1), end_date: _fmtYmd(py, pm, _daysInMonth(py, pm)) };
};
const computeLastNDays = (n) => {
  const t = today();
  const { y, m, d } = _parseYmd(t);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - (n - 1));
  return {
    start_date: _fmtYmd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate()),
    end_date: t,
  };
};
const detectPreset = (start, end) => {
  if (!start || !end) return "custom";
  const tm = computeThisMonth();
  if (start === tm.start_date && end === tm.end_date) return "this_month";
  const lm = computeLastMonth();
  if (start === lm.start_date && end === lm.end_date) return "last_month";
  const l7 = computeLastNDays(7);
  if (start === l7.start_date && end === l7.end_date) return "last_7";
  const l30 = computeLastNDays(30);
  if (start === l30.start_date && end === l30.end_date) return "last_30";
  return "custom";
};

// Feb 2026 — Header periode untuk top-bar Rekap Absensi. Format
// Indonesia (ringkas): jika start & end di bulan+tahun yang sama →
// "15–21 September 2026"; jika beda bulan tapi tahun sama →
// "23 Agustus–21 September 2026"; jika beda tahun → sertakan tahun di
// kedua sisi. Screenshot/print-friendly: tidak menggunakan simbol
// asing, hanya en-dash. Toleran terhadap input invalid → return "".
const PRESET_LABEL = {
  this_month: "Bulan Ini",
  last_month: "Bulan Lalu",
  last_7: "7 Hari Terakhir",
  last_30: "30 Hari Terakhir",
  custom: "Custom",
};
const _MONTH_ID = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
const formatPeriodeID = (start, end) => {
  if (!start || !end) return "";
  const s = _parseYmd(start);
  const e = _parseYmd(end);
  if (!s.y || !e.y) return "";
  const sMon = _MONTH_ID[s.m - 1];
  const eMon = _MONTH_ID[e.m - 1];
  if (s.y === e.y && s.m === e.m) {
    return `${s.d}–${e.d} ${sMon} ${s.y}`;
  }
  if (s.y === e.y) {
    return `${s.d} ${sMon}–${e.d} ${eMon} ${s.y}`;
  }
  return `${s.d} ${sMon} ${s.y}–${e.d} ${eMon} ${e.y}`;
};

const inputCls = "h-10 w-full px-3 rounded-md border border-[#E5E7EB] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]";

export default function AttendanceReport() {
  const { user } = useAuth();
  // Koordinator has permanent READ-ONLY access to this page via the
  // `attendance_recap_view` privilege. Only Admin retains the "Koreksi
  // Absensi" write action — backend `/attendance/correct/*` also enforces
  // `require_admin`, this flag is a UX gate to hide the button entirely.
  const canCorrect = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link seed: when opened from an admin notification (Absensi Perlu
  // Verifikasi toast/bell), the URL carries filters so we land on the exact
  // row without an extra click. Read once on mount; subsequent changes go
  // through the normal filter form.
  const initialFilters = () => {
    // Default range: month-to-date in Asia/Jakarta. Deep-link params from
    // the notification bell still take precedence so admins land exactly
    // on the flagged record.
    const sd = searchParams.get("start_date") || firstOfMonth();
    const ed = searchParams.get("end_date") || today();
    return {
      start_date: sd,
      end_date: ed,
      user_id: searchParams.get("user_id") || "",
      location_id: searchParams.get("location_id") || "",
      status: searchParams.get("status") || "",
      role: searchParams.get("role") || "",
      duty_id: searchParams.get("duty_id") || "",
      wilayah_id: searchParams.get("wilayah_id") || "",
    };
  };
  const [filters, setFilters] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25 });
  const [loading, setLoading] = useState(true);
  const reqSeq = useRef(0);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  // Feb 2026 — DINAS filter options untuk dropdown "Filter DINAS".
  // Sumber: /attendance/duties (scoped by RBAC existing di backend, aman
  // untuk koord & admin). Diambil sekali saat mount; teknisi tidak
  // memiliki privilege `attendance_recap_view` sehingga page ini tidak
  // dibuka olehnya.
  const [duties, setDuties] = useState([]);
  // Feb 2026 — Wilayah filter options untuk dropdown "Filter Wilayah".
  // Sumber: /reference (master data) atau /wilayah endpoint.
  const [wilayahList, setWilayahList] = useState([]);
  // Feb 2026 — Facet counts untuk dropdown Wilayah & DINAS (preview
  // jumlah baris per opsi). Refresh setiap `applied` berubah supaya
  // count sinkron dengan filter aktif. Exclude-self semantik ditangani
  // backend `/attendance/report/facets`.
  const [facets, setFacets] = useState({ by_wilayah: {}, by_duty: {}, by_user: {}, by_status: {}, by_role: {}, by_location: {} });
  const [detail, setDetail] = useState(null);
  const [correction, setCorrection] = useState(null); // {rec, form}
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Monthly summary cards — visible only when filter narrows to a single
  // user AND a single calendar month (so IZIN/SAKIT/CUTI/ALPA counts are
  // meaningful). Uses the existing /attendance/monthly-summary endpoint
  // (Fase 2). Auto-refreshes when the filter is applied.
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const summaryScope = useMemo(() => {
    if (!applied.user_id || !applied.start_date || !applied.end_date) return null;
    const [sy, sm] = applied.start_date.split("-");
    const [ey, em] = applied.end_date.split("-");
    if (sy !== ey || sm !== em) return null;  // period must be single month
    return { user_id: applied.user_id, year: parseInt(sy, 10), month: parseInt(sm, 10) };
  }, [applied]);

  useEffect(() => {
    if (!summaryScope) { setSummary(null); return; }
    let cancelled = false;
    setSummaryLoading(true);
    api.get("/attendance/monthly-summary", { params: summaryScope })
      .then(({ data }) => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [summaryScope]);

  useEffect(() => {
    // Fetch the user picker options via the attendance-scoped endpoint so
    // koordinator only sees teknisi in their wilayah — admin still gets
    // everyone. Falls back to the admin-only /users when the scoped
    // endpoint isn't available (older backend).
    Promise.all([
      api.get("/attendance/scope-users").catch(() => api.get("/users").catch(() => ({ data: [] }))),
      api.get("/attendance/locations").catch(() => ({ data: [] })),
      api.get("/attendance/duties").catch(() => ({ data: { items: [] } })),
      api.get("/settings/wilayah").catch(() => ({ data: [] })),
    ]).then(([u, l, d, w]) => {
      setUsers(u.data || []);
      setLocations(l.data || []);
      setDuties((d.data && d.data.items) || []);
      // /wilayah returns list of {id, code, label, order}
      setWilayahList(Array.isArray(w.data) ? w.data : (w.data?.items || []));
    });
  }, []);

  const load = async (opts = {}) => {
    // Hanya respons request terbaru yang boleh memperbarui tabel/facets.
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const params = { ...applied, page: opts.page ?? page, limit: 25 };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const { data: d } = await api.get("/attendance/report", { params });
      if (seq !== reqSeq.current) return;
      setData(d);
      // Feb 2026 — Facet counts untuk dropdown preview. Non-blocking:
      // dilakukan bersamaan (Promise.all) tapi failure di-swallow supaya
      // tidak crash halaman utama.
      const facetParams = { ...applied };
      Object.keys(facetParams).forEach((k) => { if (!facetParams[k]) delete facetParams[k]; });
      api.get("/attendance/report/facets", { params: facetParams })
        .then(({ data: f }) => seq === reqSeq.current && setFacets({
          by_wilayah: f.by_wilayah || {},
          by_duty: f.by_duty || {},
          by_user: f.by_user || {},
          by_status: f.by_status || {},
          by_role: f.by_role || {},
          by_location: f.by_location || {},
        }))
        .catch(() => seq === reqSeq.current && setFacets({ by_wilayah: {}, by_duty: {}, by_user: {}, by_status: {}, by_role: {}, by_location: {} }));
    } catch (e) {
      if (seq === reqSeq.current) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memuat rekap");
    } finally { if (seq === reqSeq.current) setLoading(false); }
  };
  useEffect(() => { load({ page }); /* eslint-disable-next-line */ }, [applied, page]);

  const applyFilters = () => { setPage(1); setApplied({ ...filters }); };
  const resetFilters = () => {
    // Reset mirrors the initial mount: month-to-date in WIB, no user/loc/status/role/duty/wilayah.
    const f = { start_date: firstOfMonth(), end_date: today(), user_id: "", location_id: "", status: "", role: "", duty_id: "", wilayah_id: "" };
    setFilters(f); setApplied(f); setPage(1);
  };

  // Excel export — reuses the SAME filter parameters as the report table so
  // admin gets exactly what they see (no pagination limit). Backend returns
  // a streamed XLSX; frontend triggers browser download via a Blob URL.
  const exportExcel = async () => {
    if (exporting) return;
    if ((data.total || 0) === 0) {
      toast.error("Tidak ada data absensi untuk diekspor pada filter ini.");
      return;
    }
    setExporting(true);
    try {
      const params = { ...applied };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const resp = await api.get("/attendance/report/export", { params, responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sd = applied.start_date || "all";
      const ed = applied.end_date || "all";
      a.download = `Rekap_Absensi_${sd}_${ed}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("Excel berhasil diunduh");
    } catch (e) {
      toast.error("Gagal mengekspor rekap absensi. Silakan coba lagi.");
    } finally {
      setExporting(false);
    }
  };

  // PDF export — same filter contract as Excel; opens a landscape A4 PDF
  // that HR can share directly (no spreadsheet editing needed). Mirrors the
  // download-blob pattern above so both formats behave identically for the
  // user.
  const exportPdf = async () => {
    if (exportingPdf) return;
    if ((data.total || 0) === 0) {
      toast.error("Tidak ada data absensi untuk diekspor pada filter ini.");
      return;
    }
    setExportingPdf(true);
    try {
      const params = { ...applied };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const resp = await api.get("/attendance/report/export.pdf", { params, responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sd = applied.start_date || "all";
      const ed = applied.end_date || "all";
      a.download = `Rekap_Absensi_${sd}_${ed}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("PDF berhasil diunduh");
    } catch (e) {
      toast.error("Gagal mengekspor PDF. Silakan coba lagi.");
    } finally {
      setExportingPdf(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.limit || 25)));

  return (
    <div className="p-4 md:p-6 max-w-6xl 2xl:max-w-[1600px] mx-auto space-y-6" data-testid="attendance-report-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Operasional</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Rekap Absensi</h1>
        <p className="text-sm text-[#6B7280] mt-1">Pantau dan audit kehadiran teknisi serta koordinator.</p>
        {/* Feb 2026 — Header periode: mengikuti `applied.*` (bukan draft
            `filters.*`) supaya konsisten dengan data yang sedang
            ditampilkan di tabel/summary/facets. Terlihat di screenshot
            dan print (weight semibold, kontras cukup pada background
            putih). */}
        {applied?.start_date && applied?.end_date && (
          <div className="mt-2 text-sm font-semibold text-[#111827]" data-testid="rekap-header-periode">
            Rekap Absensi · {PRESET_LABEL[detectPreset(applied.start_date, applied.end_date)]} · {formatPeriodeID(applied.start_date, applied.end_date)}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-4 md:p-5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-[minmax(0,0.9fr)_repeat(2,minmax(0,1.15fr))_minmax(0,0.75fr)_repeat(2,minmax(0,1fr))_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1.3fr)] gap-3 2xl:gap-2" data-testid="attendance-filter-grid">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Periode</label>
            <select
              value={detectPreset(filters.start_date, filters.end_date)}
              onChange={(e) => {
                const v = e.target.value;
                let range = null;
                if (v === "this_month") range = computeThisMonth();
                else if (v === "last_month") range = computeLastMonth();
                else if (v === "last_7") range = computeLastNDays(7);
                else if (v === "last_30") range = computeLastNDays(30);
                // "custom" → no date change (user akan edit manual)
                if (range) {
                  const next = { ...filters, ...range };
                  setFilters(next);
                  setApplied(next);
                  setPage(1);
                }
              }}
              className={inputCls}
              data-testid="filter-periode"
            >
              <option value="this_month">Bulan Ini</option>
              <option value="last_month">Bulan Lalu</option>
              <option value="last_7">7 Hari Terakhir</option>
              <option value="last_30">30 Hari Terakhir</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Tanggal Mulai</label>
            <input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className={inputCls} data-testid="filter-start" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Tanggal Selesai</label>
            <input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className={inputCls} data-testid="filter-end" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Role</label>
            <select value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} className={inputCls} data-testid="filter-role">
              <option value="">Semua</option>
              <option value="teknisi">Teknisi{facets.by_role?.teknisi ? ` (${facets.by_role.teknisi})` : ""}</option>
              <option value="koordinator">Koordinator{facets.by_role?.koordinator ? ` (${facets.by_role.koordinator})` : ""}</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">User</label>
            <select value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} className={inputCls} data-testid="filter-user">
              <option value="">Semua</option>
              {users
                .filter(u => u.absensi_enabled)
                .filter(u => !filters.role || (u.role || "").toLowerCase() === filters.role)
                .map((u) => {
                  const c = facets.by_user?.[u.id] || 0;
                  return <option key={u.id} value={u.id}>{u.name} ({u.username}){c ? ` · ${c}` : ""}</option>;
                })}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Lokasi</label>
            <select value={filters.location_id} onChange={(e) => setFilters({ ...filters, location_id: e.target.value })} className={inputCls} data-testid="filter-location">
              <option value="">Semua</option>
              {locations.map((l) => {
                const c = facets.by_location?.[l.id] || 0;
                return <option key={l.id} value={l.id}>{l.name}{c ? ` (${c})` : ""}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Wilayah</label>
            <select value={filters.wilayah_id} onChange={(e) => setFilters({ ...filters, wilayah_id: e.target.value })} className={inputCls} data-testid="filter-wilayah">
              <option value="">Semua</option>
              {wilayahList.map((w) => {
                const c = facets.by_wilayah?.[w.id] || 0;
                return (
                  <option key={w.id} value={w.id}>{(w.label || w.code)}{c ? ` (${c})` : ""}</option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">DINAS</label>
            <select value={filters.duty_id} onChange={(e) => setFilters({ ...filters, duty_id: e.target.value })} className={inputCls} data-testid="filter-duty">
              <option value="">Semua</option>
              <option value="NONE">Kantor (non-DINAS){facets.by_duty?.NONE ? ` (${facets.by_duty.NONE})` : ""}</option>
              {duties.map((d) => {
                const sd = d.start_date || d.duty_date || "";
                const ed = d.end_date || sd;
                const period = sd === ed ? sd : `${sd}..${ed}`;
                const c = facets.by_duty?.[d.id] || 0;
                return (
                  <option key={d.id} value={d.id}>
                    {d.tujuan} ({period}){c ? ` · ${c}` : ""}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={inputCls} data-testid="filter-status">
              <option value="">Semua</option>
              <optgroup label="Kehadiran">
                <option value="HADIR">Hadir{facets.by_status?.HADIR ? ` (${facets.by_status.HADIR})` : ""}</option>
                <option value="TERLAMBAT">Terlambat{facets.by_status?.TERLAMBAT ? ` (${facets.by_status.TERLAMBAT})` : ""}</option>
                <option value="ALPA">Alpa{facets.by_status?.ALPA ? ` (${facets.by_status.ALPA})` : ""}</option>
              </optgroup>
              <optgroup label="Ketidakhadiran">
                <option value="IZIN">Izin{facets.by_status?.IZIN ? ` (${facets.by_status.IZIN})` : ""}</option>
                <option value="SAKIT">Sakit{facets.by_status?.SAKIT ? ` (${facets.by_status.SAKIT})` : ""}</option>
                <option value="CUTI">Cuti{facets.by_status?.CUTI ? ` (${facets.by_status.CUTI})` : ""}</option>
              </optgroup>
              <optgroup label="Lifecycle">
                <option value="BELUM_CHECKIN">Belum Check-in</option>
                <option value="BELUM_PULANG">Belum Pulang</option>
                <option value="IN_PROGRESS">In Progress{facets.by_status?.IN_PROGRESS ? ` (${facets.by_status.IN_PROGRESS})` : ""}</option>
                <option value="DONE">Selesai{facets.by_status?.DONE ? ` (${facets.by_status.DONE})` : ""}</option>
                <option value="PERLU_VERIFIKASI">Perlu Verifikasi{facets.by_status?.PERLU_VERIFIKASI ? ` (${facets.by_status.PERLU_VERIFIKASI})` : ""}</option>
              </optgroup>
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:flex-wrap">
          <button onClick={resetFilters} className="inline-flex items-center justify-center gap-1.5 h-10 sm:h-9 px-3 rounded-md border border-[#E5E7EB] hover:bg-[#F8F9FA] text-sm font-semibold" data-testid="btn-reset">
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
          <button onClick={applyFilters} className="inline-flex items-center justify-center gap-1.5 h-10 sm:h-9 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-bold" data-testid="btn-apply">
            <Filter className="h-3.5 w-3.5" /> Terapkan Filter
          </button>
          <button
            onClick={exportExcel}
            disabled={exporting || exportingPdf || loading}
            className="inline-flex items-center justify-center gap-1.5 h-10 sm:h-9 px-4 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold disabled:opacity-50"
            data-testid="btn-export-excel"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
            {exporting ? "Menyiapkan Excel…" : "Export Excel"}
          </button>
          <button
            onClick={exportPdf}
            disabled={exporting || exportingPdf || loading}
            className="inline-flex items-center justify-center gap-1.5 h-10 sm:h-9 px-4 rounded-md bg-[#991B1B] hover:bg-[#7F1D1D] text-white text-sm font-bold disabled:opacity-50"
            data-testid="btn-export-pdf"
          >
            {exportingPdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            {exportingPdf ? "Menyiapkan PDF…" : "Export PDF"}
          </button>
        </div>
      </div>

      {/* Monthly summary — appears when a specific user + single month
          is filtered. Shows the same numbers backend uses for Hari Potong
          so admin/koord don't need to open the endpoint to check. */}
      {summaryScope && (
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-4" data-testid="monthly-summary-card">
          <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">
              Ringkasan Bulanan · {summaryScope.year}-{String(summaryScope.month).padStart(2, "0")}
            </div>
            {summary && (
              <div className="text-[10px] font-mono-tab text-[#6B7280]">
                Formula: HariPotong = max(0, Total − {summary.batas})
              </div>
            )}
          </div>
          {summaryLoading && <div className="text-sm text-[#9CA3AF] flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Memuat ringkasan…</div>}
          {!summaryLoading && summary && (() => {
            const cards = [
              { key: "IZIN",  label: "Izin",       val: summary.counts.IZIN,       cls: "bg-[#DBEAFE] text-[#1E3A8A]" },
              { key: "SAKIT", label: "Sakit",      val: summary.counts.SAKIT,      cls: "bg-[#FFE4E6] text-[#9F1239]" },
              { key: "CUTI",  label: "Cuti",       val: summary.counts.CUTI,       cls: "bg-[#E9D5FF] text-[#6B21A8]" },
              { key: "ALPA",  label: "Alpa",       val: summary.counts.ALPA,       cls: "bg-[#FEE2E2] text-[#7F1D1D]" },
              { key: "TOTAL", label: "Total Tidak Masuk", val: summary.total_tidak_masuk, cls: "bg-[#F3F4F6] text-[#111827]" },
              { key: "POTONG", label: "Hari Potong", val: summary.hari_potong, cls: (summary.hari_potong > 0 ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#ECFDF5] text-[#065F46]") },
            ];
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {cards.map((c) => (
                  <div key={c.key} className={`rounded-md p-3 ${c.cls}`} data-testid={`summary-${c.key.toLowerCase()}`}>
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-70">{c.label}</div>
                    <div className="font-display text-2xl font-black tracking-tighter mt-0.5 font-mono-tab">{c.val}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          {!summaryLoading && !summary && (
            <div className="text-sm text-[#9CA3AF]">Tidak dapat memuat ringkasan.</div>
          )}
        </div>
      )}

      {/* Table (desktop) / Cards (mobile) */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center gap-3 text-[#6B7280]"><Loader2 className="h-4 w-4 animate-spin" /> Memuat rekap absensi…</div>
        ) : (data.items?.length ?? 0) === 0 ? (
          <div className="p-10 text-center text-sm text-[#9CA3AF]" data-testid="empty-report">
            Belum ada data absensi pada filter ini.
            {user?.role === "koordinator" && (user.allowed_location_ids || []).length === 0 && (
              <div className="mt-4 max-w-xl mx-auto rounded-md border border-[#FCD34D] bg-[#FEF3C7] p-3 text-left text-[#92400E]" data-testid="empty-no-wilayah-hint">
                <div className="font-bold text-xs uppercase tracking-widest mb-1">Lokasi absensi belum diatur</div>
                <div className="text-xs leading-relaxed">
                  Anda belum memiliki lokasi absensi sehingga tidak ada teknisi yang berada dalam cakupan Rekap Absensi Anda.
                  Hubungi <strong>admin</strong> untuk meng-<em>assign</em> lokasi pada profil Anda melalui menu <strong>Manajemen User → tombol Lokasi</strong>.
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#F8F9FA] text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                  <tr>
                    <th className="px-4 py-3">Tanggal</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Lokasi</th>
                    <th className="px-4 py-3">Masuk</th>
                    <th className="px-4 py-3">Pulang</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Source Pulang</th>
                    <th className="px-4 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.id} className="border-t border-[#F3F4F6]" data-testid={`report-row-${r.id}`}>
                      <td className="px-4 py-3 font-mono-tab">{r.attendance_date}</td>
                      <td className="px-4 py-3"><div className="font-semibold">{r.user_name || r.username}</div><div className="text-[10px] text-[#9CA3AF] font-mono-tab">{r.username}</div></td>
                      <td className="px-4 py-3 uppercase text-xs">{r.user_role}</td>
                      <td className="px-4 py-3">{r.attendance_location_name} <span className="text-[10px] text-[#9CA3AF] font-mono-tab">· {r.attendance_location_code}</span></td>
                      <td className="px-4 py-3 font-mono-tab">{fmtTime(r.check_in_at)}</td>
                      <td className="px-4 py-3 font-mono-tab">{r.check_out_at ? fmtTime(r.check_out_at) : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex h-6 px-2 items-center rounded-full border text-[10px] font-bold ${STATUS_CLS[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</span>
                        {(r.corrections?.length ?? 0) > 0 && (
                          <span
                            title={`Dikoreksi ${r.corrections.length}× — buka detail untuk melihat riwayat`}
                            className="ml-1 inline-flex h-5 px-1.5 items-center rounded-full border border-[#93C5FD] bg-[#DBEAFE] text-[#1E3A8A] text-[9px] font-bold uppercase tracking-wider"
                            data-testid={`chip-koreksi-${r.id}`}
                          >
                            Koreksi · {r.corrections.length}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">{r.check_out_source ? (SOURCE_LABEL[r.check_out_source] || r.check_out_source) : "—"}</td>
                      <td className="px-4 py-3 text-right">{r.synthetic ? <span className="text-[10px] text-[#9CA3AF]">—</span> : <button onClick={() => setDetail(r)} className="inline-flex items-center gap-1 h-7 px-2 rounded-md hover:bg-[#F3F4F6] text-xs font-semibold text-[#0055FF]" data-testid={`btn-detail-${r.id}`}><Eye className="h-3.5 w-3.5" />Detail</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards — Fase 3.6: card layout untuk viewport <md
                agar tidak perlu horizontal scroll. Data source & business
                logic 100% identik dengan tabel desktop (dari `data.items`).
                Tombol Detail selalu tersedia; tombol Koreksi hanya untuk
                admin (mengikuti `canCorrect`). */}
            <ul className="md:hidden divide-y divide-[#F3F4F6]" data-testid="mobile-attendance-list">
              {data.items.map((r) => (
                <li key={r.id} className="p-4 space-y-3" data-testid={`report-card-${r.id}`}>
                  {/* Header: nama + role kiri, status + koreksi chip kanan */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-bold break-words">{r.user_name || r.username}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">{r.user_role}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`inline-flex h-6 px-2 items-center rounded-full border text-[10px] font-bold whitespace-nowrap ${STATUS_CLS[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</span>
                      {(r.corrections?.length ?? 0) > 0 && (
                        <span
                          className="inline-flex h-5 px-1.5 items-center gap-1 rounded-full border border-[#93C5FD] bg-[#DBEAFE] text-[#1E3A8A] text-[9px] font-bold uppercase whitespace-nowrap"
                          data-testid={`m-chip-koreksi-${r.id}`}
                          title={`Dikoreksi ${r.corrections.length}× — buka detail untuk melihat riwayat`}
                        >
                          Koreksi · {r.corrections.length}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Detail fields with labels */}
                  <div className="border-t border-[#E5E7EB] pt-3 space-y-2.5 text-xs">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Tanggal</div>
                      <div className="font-mono-tab">{r.attendance_date}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Lokasi</div>
                      <div className="break-words whitespace-normal">
                        {r.attendance_location_name
                          ? <>{r.attendance_location_name} <span className="text-[#9CA3AF]">· {r.attendance_location_code}</span></>
                          : <span className="text-[#9CA3AF]">—</span>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Masuk</div>
                        <div className="font-mono-tab">{r.check_in_at ? fmtTime(r.check_in_at) : <span className="text-[#9CA3AF]">—</span>}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Pulang</div>
                        <div className="font-mono-tab">{r.check_out_at ? fmtTime(r.check_out_at) : <span className="text-[#9CA3AF]">—</span>}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Source Pulang</div>
                      <div className="break-words whitespace-normal">
                        {r.check_out_source
                          ? (SOURCE_LABEL[r.check_out_source] || r.check_out_source)
                          : <span className="text-[#9CA3AF]">—</span>}
                      </div>
                    </div>
                  </div>

                  {/* Aksi: Detail (semua) + Koreksi (admin only, reuse dialog existing via detail state) */}
                  <div className="border-t border-[#E5E7EB] pt-3 grid grid-cols-1 gap-2">
                    <button
                      onClick={() => setDetail(r)}
                      data-testid={`m-btn-detail-${r.id}`}
                      className="h-10 rounded-md border border-[#E5E7EB] hover:bg-[#F8F9FA] text-sm font-semibold text-[#0055FF] flex items-center justify-center gap-1.5"
                    >
                      <Eye className="h-4 w-4" /> Detail
                    </button>
                    {canCorrect && (
                      <button
                        onClick={() => setCorrection({ rec: r, form: { check_in_at: r.check_in_at || "", check_out_at: r.check_out_at || "", status: r.status || "", reason: "" } })}
                        data-testid={`m-btn-koreksi-${r.id}`}
                        className="h-10 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-bold flex items-center justify-center gap-1.5"
                      >
                        Koreksi Absensi
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {/* Pagination */}
            <div className="p-3 border-t border-[#E5E7EB] flex items-center justify-between text-xs text-[#6B7280] flex-wrap gap-2">
              <div>Total: <b className="font-mono-tab">{data.total}</b> · Halaman <b className="font-mono-tab">{data.page}</b> / <span className="font-mono-tab">{totalPages}</span></div>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))} className="h-8 px-2 rounded-md border border-[#E5E7EB] disabled:opacity-40" data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></button>
                <button disabled={page >= totalPages || loading} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="h-8 px-2 rounded-md border border-[#E5E7EB] disabled:opacity-40" data-testid="page-next"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Detail Absensi</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto pr-1">
              <Section title="User">
                <Row k="Nama" v={detail.user_name} />
                <Row k="Username" v={detail.username} />
                <Row k="Role" v={(detail.user_role || "").toUpperCase()} />
              </Section>
              <Section title="Absensi">
                <Row k="Tanggal" v={detail.attendance_date} />
                <Row k="Lokasi" v={`${detail.attendance_location_name} · ${detail.attendance_location_code}`} />
                <Row k="Masuk" v={detail.check_in_at ? `${fmtTime(detail.check_in_at)} WIB` : "—"} />
                <Row k="Pulang" v={detail.check_out_at ? `${fmtTime(detail.check_out_at)} WIB` : "—"} />
                <Row k="Status" v={STATUS_LABEL[detail.status] || detail.status} />
                <Row k="Source Pulang" v={detail.check_out_source ? (SOURCE_LABEL[detail.check_out_source] || detail.check_out_source) : "—"} />
              </Section>
              <Section title="GPS Evidence">
                <Row k="Masuk lat/lng/acc" v={detail.check_in_lat ? `${detail.check_in_lat}, ${detail.check_in_lng} · ${Math.round(detail.check_in_accuracy || 0)}m` : "—"} />
                <Row k="Pulang lat/lng/acc" v={detail.check_out_lat ? `${detail.check_out_lat}, ${detail.check_out_lng} · ${Math.round(detail.check_out_accuracy || 0)}m` : "—"} />
                <Row k="Last inside" v={detail.last_inside_at ? `${fmtTime(detail.last_inside_at)} WIB` : "—"} />
                <Row k="Distance masuk" v={detail.check_in_distance != null ? `${Math.round(detail.check_in_distance)}m` : "—"} />
              </Section>
              <Section title="Sistem">
                <Row k="Created" v={fmtDate(detail.created_at)} />
                <Row k="Updated" v={fmtDate(detail.updated_at)} />
                <Row k="Checkout recorded" v={detail.checkout_recorded_at ? fmtDate(detail.checkout_recorded_at) : "—"} />
              </Section>
              {(detail.corrections?.length ?? 0) > 0 && (
                <Section title={`Riwayat Koreksi (${detail.corrections.length})`}>
                  <ul className="space-y-2">
                    {detail.corrections.map((c, i) => (
                      <li key={i} className="rounded-md border border-[#E5E7EB] p-2 text-xs" data-testid={`correction-entry-${i}`}>
                        <div className="flex justify-between gap-2 text-[10px] uppercase text-[#9CA3AF]"><span>{c.by_name || c.by}</span><span className="font-mono-tab">{fmtDate(c.at)}</span></div>
                        <div className="mt-1"><b>Alasan:</b> {c.reason}</div>
                        <pre className="mt-1 bg-[#F8F9FA] rounded p-2 text-[10px] overflow-x-auto">{JSON.stringify({ before: c.before, after: c.after }, null, 2)}</pre>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>
          )}
          <DialogFooter>
            {detail && canCorrect && (
              <button
                onClick={() => setCorrection({ rec: detail, form: { check_in_at: detail.check_in_at || "", check_out_at: detail.check_out_at || "", status: detail.status || "", reason: "" } })}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-bold"
                data-testid="btn-open-correction"
              >
                <PencilLine className="h-3.5 w-3.5" /> Koreksi Absensi
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction dialog */}
      <CorrectionDialog
        state={correction}
        onClose={() => setCorrection(null)}
        onSaved={async () => { setCorrection(null); setDetail(null); await load({ page }); toast.success("Koreksi tersimpan"); }}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1.5">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between gap-3 text-sm"><span className="text-[#6B7280]">{k}</span><span className="font-semibold text-right break-all">{v ?? "—"}</span></div>;
}

// Split an ISO string in WIB into { date: "YYYY-MM-DD", time: "HH:MM" } for
// use in native <input type="date"> + <input type="time">. Returns empty
// strings when the input is falsy. Handles both "+07:00" and Z offsets.
function isoToWibParts(iso) {
  if (!iso) return { date: "", time: "" };
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return { date: "", time: "" };
    const date = d.toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }); // YYYY-MM-DD
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" });
    return { date, time };
  } catch { return { date: "", time: "" }; }
}
// Combine picker parts back into a WIB ISO (with +07:00 offset). Returns
// null if either part is empty so the API PATCH knows to clear the field.
function wibPartsToIso(date, time) {
  if (!date || !time) return null;
  const [hh, mm] = time.split(":");
  return `${date}T${hh}:${mm}:00+07:00`;
}

function CorrectionDialog({ state, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ci_date: "", ci_time: "", co_date: "", co_time: "", status: "", reason: "" });
  useEffect(() => {
    if (!state) return;
    const ci = isoToWibParts(state.rec.check_in_at);
    const co = isoToWibParts(state.rec.check_out_at);
    setForm({
      ci_date: ci.date, ci_time: ci.time,
      co_date: co.date, co_time: co.time,
      status: state.rec.status || "",
      reason: "",
    });
  }, [state]);
  // Regex 24-jam untuk validasi + gate submit — nilai form tetap "HH:mm".
  const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
  const ciTimeInvalid = !!form.ci_time && !HHMM_RE.test(form.ci_time);
  const coTimeInvalid = !!form.co_time && !HHMM_RE.test(form.co_time);
  const submit = async () => {
    if (!form.reason || form.reason.trim().length < 5) {
      toast.error("Alasan minimal 5 karakter");
      return;
    }
    if (ciTimeInvalid || coTimeInvalid) {
      toast.error("Format jam tidak valid. Gunakan HH:mm (00-23:00-59)");
      return;
    }
    // Validate: if user provided time but not date (or vice versa) for a field.
    if ((form.ci_date && !form.ci_time) || (!form.ci_date && form.ci_time)) { toast.error("Jam Masuk butuh tanggal + jam lengkap"); return; }
    if ((form.co_date && !form.co_time) || (!form.co_date && form.co_time)) { toast.error("Jam Pulang butuh tanggal + jam lengkap"); return; }
    setSaving(true);
    try {
      const newCI = wibPartsToIso(form.ci_date, form.ci_time);
      const newCO = wibPartsToIso(form.co_date, form.co_time);
      const body = { reason: form.reason.trim() };
      // Only send fields that actually changed vs. the existing record.
      if (newCI !== state.rec.check_in_at) body.check_in_at = newCI;
      if (newCO !== state.rec.check_out_at) body.check_out_at = newCO;
      if (form.status !== state.rec.status) body.status = form.status;
      if (Object.keys(body).length === 1) { toast.error("Tidak ada perubahan"); setSaving(false); return; }
      await api.patch(`/attendance/${state.rec.id}`, body);
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal simpan koreksi");
    } finally { setSaving(false); }
  };
  // Fase 4.6 — Masked HH:mm 24-jam handler. `<input type="time">` di
  // Chromium/Android memakai OS locale (bukan HTML lang), sehingga user
  // dengan OS en-US melihat AM/PM meski `lang="id-ID"` di source. Custom
  // masked text bypass native picker sepenuhnya. Auto-insert `:` setelah
  // 2 digit; sanitize non-digit; cap 5 char. Pattern sama dg WibDateTimePicker.
  const handleTimeChange = (currentValue, rawInput, setter) => {
    let raw = rawInput.replace(/[^0-9:]/g, "").replace(/:+/g, ":");
    if (raw.length === 2 && !raw.includes(":") && (currentValue || "").length < raw.length) {
      raw = raw + ":";
    }
    if (raw.length > 5) raw = raw.slice(0, 5);
    setter(raw);
  };
  const timeInputCls = (invalid) =>
    `${inputCls} font-mono tabular-nums text-center ${invalid ? "border-[#DC2626]" : ""}`;
  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="correction-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">Koreksi Absensi</DialogTitle>
        </DialogHeader>
        {state && (
          <div className="space-y-3">
            <div className="text-xs text-[#6B7280]">Record: <b>{state.rec.user_name}</b> · {state.rec.attendance_date}</div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Jam Masuk (WIB)</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input type="date" value={form.ci_date} onChange={(e) => setForm({ ...form, ci_date: e.target.value })} className={inputCls} data-testid="corr-ci-date" />
                {/* Custom masked HH:mm 24-jam — TIDAK memakai <input type="time"> supaya konsisten di semua OS/locale (tidak akan pernah tampil AM/PM). */}
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  placeholder="HH:mm"
                  maxLength={5}
                  value={form.ci_time}
                  onChange={(e) => handleTimeChange(form.ci_time, e.target.value, (v) => setForm({ ...form, ci_time: v }))}
                  className={timeInputCls(ciTimeInvalid)}
                  data-testid="corr-ci-time"
                  aria-label="Jam Masuk 24 jam WIB"
                />
              </div>
              {ciTimeInvalid && <p className="text-[11px] text-[#DC2626] mt-1" data-testid="corr-ci-time-error">Format jam tidak valid. Gunakan HH:mm (00-23:00-59).</p>}
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Jam Pulang (WIB)</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input type="date" value={form.co_date} onChange={(e) => setForm({ ...form, co_date: e.target.value })} className={inputCls} data-testid="corr-co-date" />
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  placeholder="HH:mm"
                  maxLength={5}
                  value={form.co_time}
                  onChange={(e) => handleTimeChange(form.co_time, e.target.value, (v) => setForm({ ...form, co_time: v }))}
                  className={timeInputCls(coTimeInvalid)}
                  data-testid="corr-co-time"
                  aria-label="Jam Pulang 24 jam WIB"
                />
              </div>
              {coTimeInvalid && <p className="text-[11px] text-[#DC2626] mt-1" data-testid="corr-co-time-error">Format jam tidak valid. Gunakan HH:mm (00-23:00-59).</p>}
              <p className="text-[10px] text-[#9CA3AF] mt-1">Kosongkan kedua field jika ingin membersihkan Jam Pulang. Format 24 jam (00:00–23:59), tanpa AM/PM.</p>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Status</label>
              <select value={form.status || ""} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="corr-status">
                <optgroup label="Kehadiran (Engine)">
                  <option value="HADIR">Hadir</option>
                  <option value="TERLAMBAT">Terlambat</option>
                  <option value="ALPA">Alpa</option>
                </optgroup>
                <optgroup label="Ketidakhadiran (Approved)">
                  <option value="IZIN">Izin</option>
                  <option value="SAKIT">Sakit</option>
                  <option value="CUTI">Cuti</option>
                </optgroup>
                <optgroup label="Lifecycle (Legacy)">
                  <option value="IN_PROGRESS">Belum Pulang</option>
                  <option value="DONE">Selesai</option>
                  <option value="PERLU_VERIFIKASI">Perlu Verifikasi</option>
                </optgroup>
              </select>
              <p className="text-[10px] text-[#9CA3AF] mt-1">HADIR/TERLAMBAT/ALPA dihitung otomatis oleh engine — koreksi manual di sini akan meng-override hasil engine dan tercatat di audit trail.</p>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5 truncate">Alasan (min 5 karakter, WAJIB)</label>
              <textarea value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} className={`${inputCls} h-auto py-2`} placeholder="Contoh: Teknisi lupa absen pulang" data-testid="corr-reason" />
              <p className="text-[10px] text-[#9CA3AF] mt-1">Audit trail: data lama akan tetap tersimpan sebagai before/after.</p>
            </div>
          </div>
        )}
        {/* Custom footer: `grid-cols-2` di mobile → tombol Batal + Simpan
            sama-lebar tinggi 44px, tidak akan tertutup native picker.
            Di ≥sm kembali `flex justify-end` seperti desktop. */}
        <div className="pt-3 mt-2 border-t border-[#E5E7EB] grid grid-cols-2 gap-2 sm:flex sm:justify-end sm:gap-2">
          <button onClick={onClose} className="h-11 sm:h-10 px-4 rounded-md border border-[#E5E7EB] text-sm font-semibold hover:bg-[#F8F9FA]" data-testid="corr-cancel">Batal</button>
          <button onClick={submit} disabled={saving} className="inline-flex items-center justify-center gap-1.5 h-11 sm:h-10 px-4 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold disabled:opacity-40" data-testid="corr-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Simpan Koreksi
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
